import { EChartsOption } from "echarts";

import { FeatureImportanceSummary } from "../types";
import { EChartsTransformer } from "./transformer";
import { formatAxisLabel, stripCodeSuffix } from "./utils/labelFormatter";

const SPEARMAN_CORRELATION_GRID_LEFT = 108;
const SPEARMAN_CORRELATION_GRID_RIGHT = 0;

export const spearmanHeatmapTransformer: EChartsTransformer<FeatureImportanceSummary> = {
  transform: (fi, wellData, chartContext) => {
    const heatmapData = fi?.spearman_heatmap;
    const isFullscreen = chartContext?.isFullscreen ?? false;
    const isSmallScreen = window.innerWidth < 1920;

    if (!heatmapData || !heatmapData.matrix || !heatmapData.columns) {
      return {};
    }

    const columns = heatmapData.columns.map(stripCodeSuffix);
    const data = [];
    for (let i = 0; i < columns.length; i++) {
      for (let j = 0; j < columns.length; j++) {
        // Exclude diagonal values (where i === j, which are always 1)
        if (i <= j && i !== j) {
          data.push([i, j, +heatmapData.matrix[i][j].toFixed(3)]);
        }
      }
    }

    const option: EChartsOption = {
      animation: false,
      tooltip: {
        show: false
      },
      grid: {
        top: 0,
        left: isFullscreen
          ? SPEARMAN_CORRELATION_GRID_LEFT + 40
          : SPEARMAN_CORRELATION_GRID_LEFT,
        right: SPEARMAN_CORRELATION_GRID_RIGHT,
        bottom: isFullscreen ? 120 : 94,
        containLabel: false
      },
      xAxis: {
        type: "category",
        data: columns,
        splitArea: { show: false },
        axisLabel: {
          fontSize: isFullscreen ? 12 : 10,
          rotate: 33,
          width: isFullscreen ? 180 : 140,
          lineHeight: isFullscreen ? 12 : 9.5,
          hideOverlap: true,
          margin: isFullscreen ? 12 : 10,
          overflow: "truncate",
          formatter: function (value) {
            return formatAxisLabel({
              value,
              dataLength: columns.length,
              isFullscreen,
              maxCharsPerLine: 31,
              maxLines: 1
            });
          }
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        nameGap: 130,
        type: "category",
        axisLabel: {
          interval: 0,
          fontSize: isFullscreen ? 12 : 10,
          rotate: 0,
          width: isFullscreen ? 140 : 100,
          overflow: "truncate", //break caused issues - added custom breaking in formatter
          lineHeight: isFullscreen ? 12 : 9.5,
          formatter: function (value) {
            return formatAxisLabel({
              value,
              dataLength: columns.length,
              isFullscreen
            });
          }
        },
        data: columns,
        splitArea: { show: false }
      },
      axisPointer: {
        label: {
          formatter: function (params) {
            return String(params.value);
          }
        }
      },
      visualMap: {
        min: -1,
        max: 1,
        calculable: false,
        orient: "vertical",
        right: 0,
        top: "center",
        precision: 3,
        z: 100,
        inRange: {
          color: [
            "#2166ac",
            "#67a9cf",
            "#d1e5f0",
            "#f7f7f7",
            "#fddbc7",
            "#ef8a62",
            "#b2182b"
          ]
        }
      },
      series: [
        {
          name: "Spearman Correlation",
          type: "heatmap",
          data: data,
          label: {
            show: true,
            fontSize: isFullscreen ? 12 : isSmallScreen ? 9 : 10,
            rotate: columns.length > 6 && isSmallScreen && !isFullscreen ? 45 : 0,
            formatter: function (params) {
              return params.data[2];
            }
          },
          itemStyle: {
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.1)"
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0, 0, 0, 0.5)"
            }
          }
        }
      ]
    };

    return option;
  }
};
